import React, { useState, useMemo, useEffect } from 'react'
import { injectIntl } from 'react-intl'
import { connect } from 'react-redux'
import { ReactComponent as ConfigureIcon } from 'assets/img/configure-icon.svg'
import Menu from 'components/common/Menu/Menu'
import { MANAGEMENT_UI_EVERYTHING } from 'constants/index'
import {
  ASSIGNEE_LABEL,
  CREATED_BY_LABEL,
  DUE_DATE_LABEL,
  FILTER_TYPE_STATUS,
  JOB_ID_LABEL,
  MANAGEMENT_UI_DATA_FIELDS,
  SCHEDULING_DATA_FIELDS,
  MEDIA_LABEL,
  MODIFIED_BY_LABEL,
  RECURRENCE_LABEL,
  SITE_NAME_LABEL,
  TASKS_COMPLETED_LABEL,
  UNITS_LABEL,
  START_DATE_LABEL,
  END_DATE_LABEL,
  CREATED_DATE_LABEL,
  COMPLETED_DATE_LABEL,
  STATUS_LABEL,
  FILTER_TYPE_DELETED,
} from 'constants/managementUI'
import { onSelectMenuOption } from 'helpers/common'
import { saveManagementUIFilters } from 'reducers/profile/reducer'
import {
  JobsHeaderColumn,
  JobsHeaderColumnJobName,
} from 'routes/App/Apps/ManagementUI/ManagementUI.styles'
import { ReactComponent as ArrowIcon } from 'assets/img/arrow-bottom.svg'
import { JobsManagementFilters } from 'types/managementUI.types'
import {
  ConfigureCol,
  IconWrapper,
  ItemHeader,
  MediaCol,
  NameCol,
  RecurrenceCol,
  TasksCompletedCol,
  ManagementUICol,
} from './JobsList.styles'
import { JobListProps } from './JobsList.types'
import messages from '../../translate'
import SingleJob from '../SingleJob/SingleJob'

export const JobsListComponent = ({
  intl: { formatMessage },
  activeTab,
  jobs,
  getStatusIcon,
  setIsDialogOpened,
  setConfirmDialogSettings,
  setJobContextMenuPosition,
  jobsManagementFilters,
  saveManagementUIFilters,
  isSchedulingTab,
  fetchManagementUITable,
  changeSortColumn,
  sortColumn,
  sortDirection,
  isLoading,
  featureFlags,
  canSeeDeletedJobs,
}: JobListProps) => {
  const { data_columns, scheduling_data_columns } = jobsManagementFilters

  const dataColumns = useMemo(
    () => (isSchedulingTab ? scheduling_data_columns : data_columns),
    [
      isSchedulingTab,
      JSON.stringify(data_columns),
      JSON.stringify(scheduling_data_columns),
    ],
  )

  const dataFields = useMemo(
    () =>
      isSchedulingTab ? SCHEDULING_DATA_FIELDS : MANAGEMENT_UI_DATA_FIELDS,
    [isSchedulingTab],
  )

  const filteredDataColumns = useMemo(
    () =>
      dataFields.filter(({ label }) =>
        dataColumns.find(column => column === label),
      ),
    [JSON.stringify(dataColumns)],
  )

  const [tmpDataColumns, setTmpDataColumns] = useState(filteredDataColumns)
  const [showFieldsMenuAnchor, setShowFieldsMenuAnchor] = useState<
    (EventTarget & SVGSVGElement) | null | HTMLElement
  >(null)

  const getDisplayStatus = (searchLabel: string) =>
    !!dataColumns.find(label => label === searchLabel)

  const assigneeColDisplay = getDisplayStatus(ASSIGNEE_LABEL)
  const recurrenceColDisplay = getDisplayStatus(RECURRENCE_LABEL)
  const mediaColDisplay = getDisplayStatus(MEDIA_LABEL)
  const jobIdColDisplay = getDisplayStatus(JOB_ID_LABEL)
  const createdByColDisplay = getDisplayStatus(CREATED_BY_LABEL)
  const modifiedByColDisplay = getDisplayStatus(MODIFIED_BY_LABEL)
  const tasksCompletedColDisplay = getDisplayStatus(TASKS_COMPLETED_LABEL)
  const dueDateColDisplay = getDisplayStatus(DUE_DATE_LABEL)
  const unitsColDisplay = getDisplayStatus(UNITS_LABEL)
  const endDateColDisplay = getDisplayStatus(END_DATE_LABEL)
  const createdDateColDisplay = getDisplayStatus(CREATED_DATE_LABEL)
  const completedDateColDisplay = getDisplayStatus(COMPLETED_DATE_LABEL)
  const startDateColDisplay = getDisplayStatus(START_DATE_LABEL)

  const onCloseDataFieldsMenu = () => {
    const saveFiltersPayload = {
      jobs_management_filters: ({
        [isSchedulingTab
          ? 'scheduling_data_columns'
          : 'data_columns']: tmpDataColumns.map(({ label }) => label),
      } as unknown) as JobsManagementFilters,
    }

    setShowFieldsMenuAnchor(null)
    saveManagementUIFilters(saveFiltersPayload)
  }

  useEffect(() => {
    setTmpDataColumns(filteredDataColumns)
  }, [JSON.stringify(filteredDataColumns)])

  const menuItems = useMemo(
    () =>
      dataFields.filter(
        ({ label, value }) =>
          !(
            (activeTab === MANAGEMENT_UI_EVERYTHING &&
              label === SITE_NAME_LABEL) ||
            value === FILTER_TYPE_STATUS
          ),
      ),
    [activeTab, JSON.stringify(dataFields)],
  )

  const isEverythingDeletedTab = Boolean(
    canSeeDeletedJobs &&
      activeTab === MANAGEMENT_UI_EVERYTHING &&
      featureFlags?.SEE_AND_RESTORE_DELETED_JOBS &&
      jobsManagementFilters[FILTER_TYPE_DELETED],
  )

  return (
    <div>
      <ItemHeader align="center">
        <NameCol>
          <JobsHeaderColumnJobName>
            {formatMessage(messages.jobName)}
          </JobsHeaderColumnJobName>
        </NameCol>
        <ManagementUICol />
        <ManagementUICol>{formatMessage(messages.site)}</ManagementUICol>
        {isEverythingDeletedTab && (
          <>
            <ManagementUICol>
              {formatMessage(messages.dateDeleted)}
            </ManagementUICol>
            <ManagementUICol>
              {formatMessage(messages.reasonForDeletion)}
            </ManagementUICol>
            <ManagementUICol>
              {formatMessage(messages.deletedBy)}
            </ManagementUICol>
          </>
        )}
        {assigneeColDisplay && (
          <ManagementUICol>{formatMessage(messages.assignee)}</ManagementUICol>
        )}
        {tasksCompletedColDisplay && (
          <TasksCompletedCol>
            {formatMessage(messages.tasksCompleted)}
          </TasksCompletedCol>
        )}
        {recurrenceColDisplay && (
          <RecurrenceCol>{formatMessage(messages.recurrence)}</RecurrenceCol>
        )}
        {mediaColDisplay && (
          <MediaCol>{formatMessage(messages.media)}</MediaCol>
        )}
        {jobIdColDisplay && (
          <ManagementUICol>{formatMessage(messages.jobId)}</ManagementUICol>
        )}
        {createdDateColDisplay && (
          <ManagementUICol>{`${formatMessage(messages.created)} ${formatMessage(
            messages.date,
          )}`}</ManagementUICol>
        )}
        {createdByColDisplay && (
          <ManagementUICol>{formatMessage(messages.createdBy)}</ManagementUICol>
        )}
        {modifiedByColDisplay && (
          <ManagementUICol>
            {formatMessage(messages.modifiedBy)}
          </ManagementUICol>
        )}
        {unitsColDisplay && (
          <ManagementUICol>{formatMessage(messages.units)}</ManagementUICol>
        )}
        {startDateColDisplay && (
          <ManagementUICol>{formatMessage(messages.startDate)}</ManagementUICol>
        )}
        {dueDateColDisplay && (
          <ManagementUICol>{formatMessage(messages.dueDate)}</ManagementUICol>
        )}
        {completedDateColDisplay && (
          <ManagementUICol>{`${formatMessage(
            messages.completed,
          )} ${formatMessage(messages.date)}`}</ManagementUICol>
        )}
        {endDateColDisplay && (
          <ManagementUICol>{formatMessage(messages.endDate)}</ManagementUICol>
        )}
        {!isSchedulingTab && (
          <JobsHeaderColumn
            align="center"
            justify="left"
            role="button"
            onClick={() => changeSortColumn(STATUS_LABEL)}>
            {formatMessage(messages.status)}
            <IconWrapper
              sortByStatusDirection={
                sortDirection && sortColumn === STATUS_LABEL
              }>
              <ArrowIcon />
            </IconWrapper>
          </JobsHeaderColumn>
        )}
        <ConfigureCol>
          <ConfigureIcon
            onClick={({ currentTarget }) => {
              setShowFieldsMenuAnchor(currentTarget)
            }}
          />
          <Menu
            multiple
            anchorEl={showFieldsMenuAnchor}
            menuItems={menuItems}
            setAnchorEl={setShowFieldsMenuAnchor}
            onClose={onCloseDataFieldsMenu}
            selectedValue={tmpDataColumns.map(({ value }) => value)}
            onClick={dataField =>
              onSelectMenuOption(
                dataField,
                { value: dataField.value, id: null },
                tmpDataColumns.find(
                  ({ label: columnLabel }) => columnLabel === dataField.label,
                ),
                tmpDataColumns,
                setTmpDataColumns,
              )
            }
            getFormatMessage={label =>
              label.includes(' ')
                ? `${formatMessage(
                    messages[label.split(' ')[0]],
                  )} ${formatMessage(messages[label.split(' ')[1]])}`
                : formatMessage(messages[label])
            }
          />
        </ConfigureCol>
      </ItemHeader>
      {jobs.map(job => (
        <SingleJob
          key={job.id}
          jobData={job}
          getStatusIcon={getStatusIcon}
          assigneeColDisplay={assigneeColDisplay}
          recurrenceColDisplay={recurrenceColDisplay}
          mediaColDisplay={mediaColDisplay}
          jobIdColDisplay={jobIdColDisplay}
          createdByColDisplay={createdByColDisplay}
          modifiedByColDisplay={modifiedByColDisplay}
          tasksCompletedColDisplay={tasksCompletedColDisplay}
          startDateColDisplay={startDateColDisplay}
          endDateColDisplay={endDateColDisplay}
          createdDateColDisplay={createdDateColDisplay}
          completedDateColDisplay={completedDateColDisplay}
          dueDateColDisplay={dueDateColDisplay}
          unitsColDisplay={unitsColDisplay}
          setIsDialogOpened={setIsDialogOpened}
          setConfirmDialogSettings={setConfirmDialogSettings}
          setJobContextMenuPosition={setJobContextMenuPosition}
          isSchedulingTab={isSchedulingTab}
          fetchManagementUITable={fetchManagementUITable}
          isLoading={isLoading}
          isEverythingDeletedTab={isEverythingDeletedTab}
        />
      ))}
    </div>
  )
}

export default connect(
  (state: {
    getIn: (
      path: Array<string>,
    ) => JobsManagementFilters | Record<string, boolean> | boolean
  }) => ({
    jobsManagementFilters: state.getIn([
      'profile',
      'profile',
      'jobsManagementFilters',
    ]) as JobsManagementFilters,
    canSeeDeletedJobs: state.getIn([
      'profile',
      'profile',
      'canSeeDeletedJobs',
    ]) as boolean,
    featureFlags: state.getIn(['profile', 'profile', 'featureFlags']) as Record<
      string,
      boolean
    >,
  }),
  {
    saveManagementUIFilters,
  },
)(injectIntl(JobsListComponent))
